"use client";

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
    type CarouselApi
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { Star } from "lucide-react";

interface Testimonial {
    quote: string;
    company: string;
    event: string;
}

interface TestimonialsCarouselProps {
    testimonials: Testimonial[];
}

export function TestimonialsCarousel({ testimonials }: TestimonialsCarouselProps) {
    const [api, setApi] = useState<CarouselApi>();
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        if (!api) return;

        api.on("select", () => {
            setCurrent(api.selectedScrollSnap());
        });
    }, [api]);

    return (
        <Carousel
            setApi={setApi}
            className="carousel-smooth relative w-full max-w-5xl mx-auto px-4 z-20
                       md:before:absolute md:before:inset-y-0 md:before:left-0 md:before:z-30 md:before:w-16 md:before:bg-gradient-to-r md:before:from-[black] md:before:to-transparent md:before:pointer-events-none
                       md:after:absolute md:after:inset-y-0 md:after:right-0 md:after:z-30 md:after:w-16 md:after:bg-gradient-to-l md:after:from-[black] md:after:to-transparent md:after:pointer-events-none"
            opts={{
                align: "center",
                loop: true,
            }}
        >
            <CarouselContent className="-ml-6">
                {testimonials.map((testimonial, index) => (
                    <CarouselItem
                        key={index}
                        className="basis-full md:basis-[65%] lg:basis-[55%] pl-6"
                    >
                        <div
                            className={cn(
                                "carousel-item group relative p-10 border border-white/10 bg-[#151515] shadow-xl rounded-sm overflow-hidden transition-opacity",
                                current === index
                                    ? "opacity-100"
                                    : "opacity-50"
                            )}
                        >
                            <div>
                                <div className="flex gap-1 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <Star
                                            key={i}
                                            className="w-4 h-4 fill-white text-white"
                                            size={16}
                                        />
                                    ))}
                                </div>
                                <p className="italic text-[#a19b92] mb-6">
                                    {testimonial.quote}
                                </p>
                                <div className="pt-4 border-t border-[#2a2825]">
                                    <p className="font-medium">{testimonial.company}</p>
                                    <p className="text-sm text-[#a19b92]">{testimonial.event}</p>
                                </div>
                            </div>
                        </div>
                    </CarouselItem>
                ))}
            </CarouselContent>
            <div className="flex justify-center gap-2 mt-4 md:hidden">
                {testimonials.map((_, index) => (
                    <button
                        key={index}
                        className={cn(
                            "w-12 h-12 rounded-full transition-colors flex items-center justify-center",
                            current === index ? "bg-white/20" : "bg-transparent hover:bg-white/10"
                        )}
                        onClick={() => api?.scrollTo(index)}
                        aria-label={`Go to slide ${index + 1}`}
                    >
                        <div className={cn(
                            "w-2 h-2 rounded-full transition-colors",
                            current === index ? "bg-white" : "bg-[#a19b92] opacity-50"
                        )} />
                    </button>
                ))}
            </div>
            <CarouselPrevious className="hidden z-20 md:flex h-6 w-[86px] left-2 md:-left-28 text-white hover:text-white/80 top-[120%] md:translate-x-[50px] bg-transparent border-none hover:bg-transparent" />
            <CarouselNext className="hidden z-20 md:flex h-6 w-[86px] right-2 md:-right-28 text-white hover:text-white/80 top-[120%] md:translate-x-[50px] bg-transparent border-none hover:bg-transparent" />
        </Carousel>
    );
}

